// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import type { BundleEntry, CarePlan, Patient } from '@medplum/fhirtypes';
import { buildRpmCarePlan, findRpmCarePlan, revokeRpmCarePlan } from '@polarissleep/shared';

export type RpmEnrollmentStatus = 'active' | 'inactive';
export const RPM_ENROLLMENT_UPDATED_EVENT = 'polarissleep:rpm-enrollment-updated';

export function notifyRpmEnrollmentUpdated(patientId: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(RPM_ENROLLMENT_UPDATED_EVENT, { detail: { patientId } }));
  }
}

function getPatientOrgId(patient: Patient): string | undefined {
  const reference = patient.meta?.account?.reference ?? patient.meta?.accounts?.[0]?.reference;
  return reference?.startsWith('Organization/') ? reference.slice('Organization/'.length) : undefined;
}

/**
 * Finds the patient's RPM enrollment CarePlan. Pass `{ fresh: true }` to bypass the client search cache when the result
 * feeds a write (e.g. a version-guarded PUT), so a CarePlan updated by another writer is not read stale.
 */
export async function findPatientRpmCarePlan(
  medplum: MedplumClient,
  patientId: string,
  options?: { fresh?: boolean }
): Promise<CarePlan | undefined> {
  const carePlans = await medplum.searchResources(
    'CarePlan',
    {
      subject: `Patient/${patientId}`,
      _count: '100',
      _sort: '-_lastUpdated',
    },
    options?.fresh ? { cache: 'no-cache' } : undefined
  );
  return findRpmCarePlan(carePlans);
}

export function getRpmEnrollmentStatus(_patient: Patient, carePlan: CarePlan | undefined): RpmEnrollmentStatus {
  return carePlan?.status === 'active' ? 'active' : 'inactive';
}

/**
 * Builds a transaction Bundle entry that activates the patient's RPM enrollment CarePlan, so callers can commit the
 * enrollment atomically with the resources that justify it (e.g. the RPM order + Initiate RPM Task, SLE-825).
 *
 * Reactivates the existing RPM CarePlan via a version-guarded PUT, or POSTs a new one when none exists. Callers must
 * invoke `notifyRpmEnrollmentUpdated(patient.id)` after the transaction succeeds.
 */
export async function buildRpmEnrollmentActivationEntry(
  medplum: MedplumClient,
  patient: Patient
): Promise<BundleEntry<CarePlan>> {
  if (!patient.id) {
    throw new Error('Cannot update RPM enrollment for Patient without id');
  }

  const existing = await findPatientRpmCarePlan(medplum, patient.id, { fresh: true });
  const carePlan = buildRpmCarePlan(patient.id, existing, getPatientOrgId(patient));

  if (carePlan.id) {
    const versionId = carePlan.meta?.versionId;
    return {
      resource: carePlan,
      request: {
        method: 'PUT',
        url: `CarePlan/${carePlan.id}`,
        ...(versionId ? { ifMatch: `W/"${versionId}"` } : {}),
      },
    };
  }
  return {
    fullUrl: `urn:uuid:${crypto.randomUUID()}`,
    resource: carePlan,
    request: { method: 'POST', url: 'CarePlan' },
  };
}

export async function setPatientRpmEnrollment(
  medplum: MedplumClient,
  patient: Patient,
  active: boolean
): Promise<{ patient: Patient; carePlan: CarePlan | undefined }> {
  if (!patient.id) {
    throw new Error('Cannot update RPM enrollment for Patient without id');
  }

  const existing = await findPatientRpmCarePlan(medplum, patient.id, { fresh: true });
  const orgId = getPatientOrgId(patient);

  if (active) {
    const carePlan = buildRpmCarePlan(patient.id, existing, orgId);
    const savedCarePlan = carePlan.id ? await medplum.updateResource(carePlan) : await medplum.createResource(carePlan);
    notifyRpmEnrollmentUpdated(patient.id);
    return { patient, carePlan: savedCarePlan };
  }

  const savedCarePlan = existing?.id ? await medplum.updateResource(revokeRpmCarePlan(existing)) : undefined;
  notifyRpmEnrollmentUpdated(patient.id);
  return { patient, carePlan: savedCarePlan };
}
